import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { searchTerms } from "@/lib/search-text";
import { getSupabaseAdmin } from "@/integrations/external-supabase/client.server";
import { resolveCurrentUserId } from "@/lib/articulos.server";

const Id = z.object({ id: z.string().uuid() });
const AgenciaRol = z.enum(["owner", "admin", "editor"]);

const agenciaSelect = "id, slug, nombre, logo_url, url_web, region, provincia_id, provincia_nombre, num_empleados, descripcion_publica, descripcion_interna, telefono, email, origen, estado, seguimiento, motivo_descarte, created_at, anuncios_count, solicitudes_count, miembros_count, tareas_count, invitaciones_count";

async function requireStaffAg(): Promise<string> {
  const userId = await resolveCurrentUserId();
  if (!userId) throw new Error("Unauthorized");
  const sb = getSupabaseAdmin();
  const { data: roles } = await sb.from("user_roles").select("role").eq("user_id", userId);
  const rs = (roles ?? []).map((r: { role: string }) => r.role);
  if (!rs.includes("admin") && !rs.includes("customer_service") && !rs.includes("agente")) {
    throw new Error("Forbidden");
  }
  return userId;
}

type AgenciaActor = {
  userId: string;
  isStaff: boolean;
  /** rol del usuario en cada agencia a la que pertenece */
  roles: Record<string, z.infer<typeof AgenciaRol>>;
};

async function resolveAgenciaActor(): Promise<AgenciaActor> {
  const userId = await resolveCurrentUserId();
  if (!userId) throw new Error("Unauthorized");
  const sb = getSupabaseAdmin();
  const [rolesRes, memsRes] = await Promise.all([
    sb.from("user_roles").select("role").eq("user_id", userId),
    sb.from("agencia_miembros").select("agencia_id, rol").eq("user_id", userId),
  ]);
  const rs = (rolesRes.data ?? []).map((r: { role: string }) => r.role);
  const isStaff =
    rs.includes("admin") || rs.includes("customer_service") || rs.includes("agente");
  const roles: AgenciaActor["roles"] = {};
  for (const m of (memsRes.data ?? []) as Array<{ agencia_id: string; rol: string }>) {
    roles[m.agencia_id] = m.rol as z.infer<typeof AgenciaRol>;
  }
  return { userId, isStaff, roles };
}

/** Staff o cualquier miembro de la agencia. */
async function requireAgenciaLectura(agenciaId: string): Promise<AgenciaActor> {
  const actor = await resolveAgenciaActor();
  if (!actor.isStaff && !actor.roles[agenciaId]) throw new Error("Forbidden");
  return actor;
}

/** Staff u owner/admin de la agencia: puede gestionar miembros e invitaciones. */
async function requireAgenciaGestion(agenciaId: string): Promise<AgenciaActor> {
  const actor = await resolveAgenciaActor();
  if (actor.isStaff) return actor;
  const rol = actor.roles[agenciaId];
  if (rol !== "owner" && rol !== "admin") throw new Error("Forbidden");
  return actor;
}

/** Solo staff u owner de la agencia pueden dar, cambiar o quitar el rol owner. */
function assertPuedeTocarOwner(
  actor: AgenciaActor,
  agenciaId: string,
  ...roles: Array<string | null | undefined>
) {
  if (actor.isStaff || actor.roles[agenciaId] === "owner") return;
  if (roles.includes("owner")) throw new Error("Solo el propietario puede gestionar el rol propietario.");
}

/** Campos internos de Bizaclia que un miembro de agencia no ve ni edita. */
const CAMPOS_INTERNOS_AGENCIA = [
  "descripcion_interna",
  "origen",
  "estado",
  "seguimiento",
  "motivo_descarte",
] as const;

function ocultarInternos<T extends Record<string, unknown>>(row: T): T {
  return { ...row, descripcion_interna: null, seguimiento: null, motivo_descarte: null };
}

function mapAgenciaRow(r: any) {
  return {
    id: r.id,
    slug: r.slug,
    nombre: r.nombre,
    logo_url: r.logo_url,
    url_web: r.url_web ?? null,
    region: r.region ?? null,
    provincia_id: r.provincia_id ?? null,
    provincia_nombre: r.provincia_nombre ?? null,
    num_empleados: r.num_empleados ?? null,
    descripcion_publica: r.descripcion_publica ?? null,
    descripcion_interna: r.descripcion_interna ?? null,
    telefono: r.telefono ?? null,
    email: r.email ?? null,
    origen: r.origen ?? "manual",
    estado: r.estado ?? "activa",
    seguimiento: r.seguimiento ?? null,
    motivo_descarte: r.motivo_descarte ?? null,
    created_at: r.created_at,
    miembros_count: r.miembros_count ?? 0,
    anuncios_count: r.anuncios_count ?? 0,
    solicitudes_count: r.solicitudes_count ?? 0,
    tareas_count: r.tareas_count ?? 0,
    invitaciones_count: r.invitaciones_count ?? 0,
  };
}

const nullableUrl = z
  .string()
  .trim()
  .max(500)
  .nullable()
  .optional()
  .or(z.literal("").transform(() => null));

const nullableText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .or(z.literal("").transform(() => null));

const AgenciaOrigen = z.enum(["registro", "lead_web", "manual", "import", "captado"]);
const AgenciaEstado = z.enum(["activa", "inactiva", "bloqueada"]);
const AgenciaSeguimiento = z.enum([
  "contrato_y_publicado",
  "contrato_firmado",
  "anuncio_publicado",
  "interesado",
  "sin_interes_claro",
  "no_interesado",
  "no_contesta",
]);

const AgenciaInput = z.object({
  nombre: z.string().trim().min(1).max(200),
  slug: z.string().trim().min(1).max(120).regex(/^[a-z0-9-]+$/),
  logo_url: nullableUrl,
  url_web: nullableUrl,
  region: nullableText(120),
  provincia_id: z.string().uuid().nullable().optional().or(z.literal("").transform(() => null)),
  num_empleados: z
    .union([z.number().int().min(0).max(100000), z.literal("").transform(() => null), z.null()])
    .nullable()
    .optional(),
  descripcion_publica: nullableText(5000),
  descripcion_interna: nullableText(5000),
  telefono: nullableText(40),
  email: nullableText(200),
  origen: AgenciaOrigen.default("manual"),
  estado: AgenciaEstado.default("activa"),
  seguimiento: AgenciaSeguimiento.nullable().optional().or(z.literal("").transform(() => null)),
  motivo_descarte: nullableText(2000),
});

function extractMediaPath(url: string | null | undefined): string | null {
  if (!url) return null;
  const match = url.match(/\/storage\/v1\/object\/public\/media\/(.+)$/);
  return match ? decodeURIComponent(match[1]) : null;
}

export const listAgencias = createServerFn({ method: "GET" }).handler(async () => {
  // Staff ve todas; un miembro de agencia, solo las suyas y sin campos internos.
  const actor = await resolveAgenciaActor();
  const propias = Object.keys(actor.roles);
  if (!actor.isStaff && propias.length === 0) throw new Error("Forbidden");
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { fetchAllPaged } = await import("@/lib/pagination.server");
  const rows = await fetchAllPaged<any>((from, to) => {
    let q = (supabaseAdmin
      .from("agencias_con_contadores" as any)
      .select(agenciaSelect) as any);
    if (!actor.isStaff) q = q.in("id", propias);
    return q.order("created_at", { ascending: false }).range(from, to);
  });
  const mapped = rows.map(mapAgenciaRow);
  return actor.isStaff ? mapped : mapped.map(ocultarInternos);
});

/** Rol del usuario actual en sus agencias (para mostrar u ocultar la gestión en la UI). */
export const getMisRolesAgencia = createServerFn({ method: "GET" }).handler(async () => {
  const { isStaff, roles } = await resolveAgenciaActor();
  return { isStaff, roles };
});


export const getAgenciaById = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => Id.parse(d))
  .handler(async ({ data }) => {
    const actor = await requireAgenciaLectura(data.id);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await (supabaseAdmin
      .from("agencias_con_contadores" as any)
      .select(agenciaSelect) as any)
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return null;
    const mapped = mapAgenciaRow(row);
    return actor.isStaff ? mapped : ocultarInternos(mapped);
  });

export const createAgencia = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => AgenciaInput.parse(d))
  .handler(async ({ data }) => {
    await requireStaffAg();
    const sb = getSupabaseAdmin();
    const { data: row, error } = await sb
      .from("agencias")
      .insert(data)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { ok: true, id: row.id };
  });

export const updateAgencia = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => AgenciaInput.extend(Id.shape).parse(d))
  .handler(async ({ data }) => {
    const actor = await requireAgenciaGestion(data.id);
    const sb = getSupabaseAdmin();
    const { id, ...rest } = data;
    // Owner/admin de agencia editan su ficha, pero no los campos internos de Bizaclia.
    if (!actor.isStaff) for (const k of CAMPOS_INTERNOS_AGENCIA) delete (rest as Record<string, unknown>)[k];
    const { error } = await sb.from("agencias").update(rest).eq("id", id);
    if (error) throw new Error(error.message);
    (await import("@/lib/catalogos-cache.server")).invalidarCatalogo("agencias");
    return { ok: true };
  });

export const deleteAgencia = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => Id.parse(d))
  .handler(async ({ data }) => {
    await requireStaffAg();
    const sb = getSupabaseAdmin();
    const { data: agencia, error: getError } = await sb
      .from("agencias")
      .select("logo_url")
      .eq("id", data.id)
      .maybeSingle();
    if (getError) throw new Error(getError.message);

    const logoPath = extractMediaPath(agencia?.logo_url ?? null);
    if (logoPath) {
      const { data: removed, error: storageError } = await sb.storage
        .from("media")
        .remove([logoPath]);
      if (storageError) throw new Error(storageError.message);
      if (!removed?.length) throw new Error(`No se pudo borrar el logo de Supabase: ${logoPath}`);
    }

    const { error } = await sb.from("agencias").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    (await import("@/lib/catalogos-cache.server")).invalidarCatalogo("agencias");
    return { ok: true };
  });

export const listAgenciaMiembros = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ agencia_id: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    const actor = await requireAgenciaLectura(data.agencia_id);
    const sb = getSupabaseAdmin();
    const { data: rows, error } = await sb
      .from("agencia_miembros")
      .select("id, user_id, rol, created_at")
      .eq("agencia_id", data.agencia_id)
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);
    const userIds = (rows ?? []).map((r) => r.user_id);
    let contactos: Record<
      string,
      {
        id: string;
        nombre: string;
        apellidos: string | null;
        email: string | null;
        telefono: string | null;
        cargo: string | null;
        slug: string | null;
      }
    > = {};
    if (userIds.length) {
      const { data: cs, error: ec } = await sb
        .from("contactos")
        .select("id, user_id, slug, nombre, apellidos, email, telefono, cargos(nombre)")
        .in("user_id", userIds);
      if (ec) throw new Error(ec.message);
      for (const c of (cs ?? []) as any[]) {
        if (c.user_id)
          contactos[c.user_id] = {
            id: c.id,
            nombre: c.nombre,
            apellidos: c.apellidos,
            email: c.email,
            telefono: c.telefono ?? null,
            cargo: c.cargos?.nombre ?? null,
            slug: c.slug ?? null,
          };
      }
    }
    const { countEmailsEnviados } = await import("@/lib/comunicaciones.server");
    const enviados = await countEmailsEnviados(
      sb,
      Object.values(contactos).map((c) => c.id),
    );
    return (rows ?? []).map((r: any) => ({
      id: r.id as string,
      user_id: r.user_id as string,
      rol: r.rol as z.infer<typeof AgenciaRol>,
      created_at: r.created_at as string,
      nombre: contactos[r.user_id]?.nombre ?? null,
      apellidos: contactos[r.user_id]?.apellidos ?? null,
      email: contactos[r.user_id]?.email ?? null,
      telefono: contactos[r.user_id]?.telefono ?? null,
      cargo: contactos[r.user_id]?.cargo ?? null,
      slug: contactos[r.user_id]?.slug ?? null,
      contacto_id: contactos[r.user_id]?.id ?? null,
      emails_enviados: contactos[r.user_id] ? (enviados[contactos[r.user_id].id] ?? 0) : 0,
      es_yo: r.user_id === actor.userId,
    }));
  });

export const addAgenciaMiembro = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        agencia_id: z.string().uuid(),
        user_id: z.string().uuid(),
        rol: AgenciaRol.default("editor"),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    await requireStaffAg();
    const sb = getSupabaseAdmin();
    const { error } = await sb.from("agencia_miembros").insert(data);
    if (error) throw new Error(error.message);
    // Vincular contacto a la agencia (si existe)
    await sb.from("contactos").update({ agencia_id: data.agencia_id }).eq("user_id", data.user_id);
    // Vincular todos los anuncios creados por este usuario a la agencia
    await sb.from("anuncios").update({ agencia_id: data.agencia_id }).eq("created_by", data.user_id);
    return { ok: true };
  });

export const updateAgenciaMiembro = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({ id: z.string().uuid(), rol: AgenciaRol }).parse(d),
  )
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const { data: miembro } = await sb
      .from("agencia_miembros")
      .select("agencia_id, rol")
      .eq("id", data.id)
      .maybeSingle();
    if (!miembro) throw new Error("Miembro no encontrado");
    const actor = await requireAgenciaGestion(miembro.agencia_id);
    assertPuedeTocarOwner(actor, miembro.agencia_id, miembro.rol, data.rol);
    const { error } = await sb
      .from("agencia_miembros")
      .update({ rol: data.rol })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const removeAgenciaMiembro = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => Id.parse(d))
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    // Recuperar miembro para conocer agencia_id y user_id antes de borrarlo
    const { data: miembro, error: eGet } = await sb
      .from("agencia_miembros")
      .select("agencia_id, user_id, rol")
      .eq("id", data.id)
      .maybeSingle();
    if (eGet) throw new Error(eGet.message);
    if (!miembro) throw new Error("Miembro no encontrado");
    const actor = await requireAgenciaGestion(miembro.agencia_id);
    assertPuedeTocarOwner(actor, miembro.agencia_id, miembro.rol);

    const { error } = await sb.from("agencia_miembros").delete().eq("id", data.id);
    if (error) throw new Error(error.message);

    if (miembro?.user_id && miembro?.agencia_id) {
      // Desvincular contacto de la agencia si coincide
      await sb
        .from("contactos")
        .update({ agencia_id: null })
        .eq("user_id", miembro.user_id)
        .eq("agencia_id", miembro.agencia_id);
    }
    return { ok: true };
  });

export const listAgenciaInvitaciones = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ agencia_id: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    await requireAgenciaGestion(data.agencia_id);
    const sb = getSupabaseAdmin();
    const { data: rows, error } = await sb
      .from("agencia_invitaciones")
      .select("id, email, rol, estado, created_at")
      .eq("agencia_id", data.agencia_id)
      .eq("estado", "pendiente")
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);

    // Resolver datos de contacto (nombre, teléfono, cargo) por email.
    const invs = (rows ?? []) as Array<{
      id: string;
      email: string;
      rol: z.infer<typeof AgenciaRol>;
      estado: string;
      created_at: string;
    }>;
    const emails = invs.map((i) => i.email).filter(Boolean);
    for (const inv of invs) Object.assign(inv, { contacto_id: null, emails_enviados: 0 });
    if (emails.length) {
      const { data: contactos } = await sb
        .from("contactos")
        .select("id, email, nombre, apellidos, telefono, cargo_id, cargos:cargo_id(nombre)")
        .in("email", emails);
      const byEmail = new Map<string, any>();
      for (const c of contactos ?? []) {
        if (c.email) byEmail.set(c.email.toLowerCase(), c);
      }
      for (const inv of invs) {
        const c = byEmail.get(inv.email.toLowerCase());
        (inv as any).nombre = c ? [c.nombre, c.apellidos].filter(Boolean).join(" ") || null : null;
        (inv as any).telefono = c?.telefono ?? null;
        (inv as any).cargo = (c as any)?.cargos?.nombre ?? null;
        (inv as any).contacto_id = c?.id ?? null;
      }
      const { countEmailsEnviados } = await import("@/lib/comunicaciones.server");
      const enviados = await countEmailsEnviados(
        sb,
        invs.map((i) => (i as any).contacto_id).filter(Boolean),
      );
      for (const inv of invs) {
        const cid = (inv as any).contacto_id as string | null;
        (inv as any).emails_enviados = cid ? (enviados[cid] ?? 0) : 0;
      }
    }
    return invs as Array<{
      id: string;
      email: string;
      rol: z.infer<typeof AgenciaRol>;
      estado: string;
      created_at: string;
      nombre: string | null;
      telefono: string | null;
      cargo: string | null;
      contacto_id: string | null;
      emails_enviados: number;
    }>;
  });

export const addAgenciaMiembroByContacto = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        agencia_id: z.string().uuid(),
        contacto_id: z.string().uuid(),
        rol: AgenciaRol.default("editor"),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const actor = await requireAgenciaGestion(data.agencia_id);
    assertPuedeTocarOwner(actor, data.agencia_id, data.rol);
    const userId = actor.userId;
    const sb = getSupabaseAdmin();
    const { data: contacto, error: eC } = await sb
      .from("contactos")
      .select("id, user_id, email, email_norm, agencia_id")
      .eq("id", data.contacto_id)
      .maybeSingle();
    if (eC) throw new Error(eC.message);
    if (!contacto) throw new Error("Contacto no encontrado");
    // Un owner/admin de agencia solo puede añadir contactos de su propia cartera.
    if (!actor.isStaff && contacto.agencia_id !== data.agencia_id) {
      throw new Error("Solo puedes añadir contactos de tu agencia.");
    }

    // Los contactos son por cartera: la ficha de la agencia puede no tener la
    // cuenta vinculada aunque la persona ya esté registrada (su ficha con
    // cuenta está en otra cartera). La invitación solo se consume al
    // registrarse, así que si ya tiene cuenta la añadimos directamente.
    let cuentaUserId = contacto.user_id;
    if (!cuentaUserId && contacto.email_norm) {
      const { data: conCuenta } = await sb
        .from("contactos")
        .select("user_id")
        .eq("email_norm", contacto.email_norm)
        .not("user_id", "is", null)
        .limit(1)
        .maybeSingle();
      cuentaUserId = conCuenta?.user_id ?? null;
    }

    if (cuentaUserId) {
      const { error } = await sb
        .from("agencia_miembros")
        .insert({ agencia_id: data.agencia_id, user_id: cuentaUserId, rol: data.rol });
      if (error && error.code !== "23505") throw new Error(error.message);
      if (contacto.user_id) {
        await sb.from("contactos").update({ agencia_id: data.agencia_id }).eq("id", contacto.id);
      } else {
        // Igual que al aceptar una invitación: su ficha sin agencia pasa a esta.
        await sb
          .from("contactos")
          .update({ agencia_id: data.agencia_id })
          .eq("user_id", cuentaUserId)
          .is("agencia_id", null);
      }
      // Mover los anuncios del usuario a la agencia solo lo decide staff.
      if (actor.isStaff) {
        await sb.from("anuncios").update({ agencia_id: data.agencia_id }).eq("created_by", cuentaUserId);
      }
      return { ok: true, tipo: "miembro" as const };
    }

    if (!contacto.email) {
      throw new Error("El contacto no tiene cuenta ni email: añade un email para poder invitarlo.");
    }
    const email = contacto.email.toLowerCase();
    const { error } = await sb
      .from("agencia_invitaciones")
      .insert({ agencia_id: data.agencia_id, email, rol: data.rol, invited_by: userId });
    if (error) {
      if (error.code === "23505") throw new Error("Ya existe una invitación pendiente para ese email.");
      throw new Error(error.message);
    }
    await sb.from("contactos").update({ agencia_id: data.agencia_id }).eq("id", contacto.id);
    return { ok: true, tipo: "invitacion" as const };
  });

export const inviteAgenciaMiembro = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        agencia_id: z.string().uuid(),
        email: z.string().trim().email().max(200),
        rol: AgenciaRol.default("editor"),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const userId = await requireStaffAg();
    const sb = getSupabaseAdmin();
    const email = data.email.toLowerCase();

    // Si ya existe una cuenta con ese email, añadirlo directamente como miembro.
    // Puede haber varias fichas con el mismo email (cartera general y de
    // agencias): priorizamos la que tiene cuenta para no invitar a quien ya
    // está registrado (la invitación solo se consume al registrarse).
    // Coincidencia exacta del email normalizado (un `ilike` trataba `_` y `%`
    // del email como comodines y podía casar con otra persona).
    const { data: contacto } = await sb
      .from("contactos")
      .select("id, user_id")
      .eq("email_norm", email.trim())
      .order("user_id", { ascending: true, nullsFirst: false })
      .limit(1)
      .maybeSingle();

    if (contacto?.user_id) {
      const { error } = await sb
        .from("agencia_miembros")
        .insert({ agencia_id: data.agencia_id, user_id: contacto.user_id, rol: data.rol });
      if (error && !error.message.includes("duplicate")) throw new Error(error.message);
      // Su ficha pasa a la agencia solo si está en la cartera general: nunca se
      // quita de la cartera de otra agencia.
      await sb
        .from("contactos")
        .update({ agencia_id: data.agencia_id })
        .eq("user_id", contacto.user_id)
        .is("agencia_id", null);
      await sb.from("anuncios").update({ agencia_id: data.agencia_id }).eq("created_by", contacto.user_id);
      return { ok: true, tipo: "miembro" as const };
    }

    const { error } = await sb
      .from("agencia_invitaciones")
      .insert({ agencia_id: data.agencia_id, email, rol: data.rol, invited_by: userId });
    if (error) {
      if (error.code === "23505") throw new Error("Ya existe una invitación pendiente para ese email.");
      throw new Error(error.message);
    }

    // No se mueve ninguna ficha sin cuenta con ese email: podría ser la de un
    // cliente (de Bizaclia o de otra agencia). Al aceptar la invitación, el
    // registro ya pasa su propia ficha a la agencia.
    return { ok: true, tipo: "invitacion" as const };
  });

async function getInvitacion(id: string): Promise<{ agencia_id: string; rol: string }> {
  const { data: inv } = await getSupabaseAdmin()
    .from("agencia_invitaciones")
    .select("agencia_id, rol")
    .eq("id", id)
    .maybeSingle();
  if (!inv) throw new Error("Invitación no encontrada");
  return inv as { agencia_id: string; rol: string };
}

export const updateAgenciaInvitacion = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid(), rol: AgenciaRol }).parse(d))
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const inv = await getInvitacion(data.id);
    const actor = await requireAgenciaGestion(inv.agencia_id);
    assertPuedeTocarOwner(actor, inv.agencia_id, inv.rol, data.rol);
    const { error } = await sb
      .from("agencia_invitaciones")
      .update({ rol: data.rol })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const cancelAgenciaInvitacion = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => Id.parse(d))
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const inv = await getInvitacion(data.id);
    const actor = await requireAgenciaGestion(inv.agencia_id);
    assertPuedeTocarOwner(actor, inv.agencia_id, inv.rol);
    const { error } = await sb
      .from("agencia_invitaciones")
      .update({ estado: "cancelada" })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

type DestinoInvitacion = {
  id: string;
  nombre: string | null;
  apellidos: string | null;
  email: string | null;
  agencia_id: string | null;
  notif_enabled?: boolean | null;
  notif_canal_email?: boolean | null;
};

/**
 * Envía el email «Invitación al equipo» a una invitación pendiente (aún sin
 * cuenta) o a un miembro ya añadido. `email` es la dirección que confirmó
 * quien envía: si la ficha ha cambiado entretanto, no se envía a otra.
 */
export const sendAgenciaInvitacionEmail = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        tipo: z.enum(["invitacion", "miembro"]),
        id: z.string().uuid(),
        email: z.string().trim().email().max(200),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    // Las plantillas y el historial de otros contactos no son legibles para un
    // admin de agencia: tras autorizarle, el envío se hace con service role.
    const { supabaseAdmin: sb } = await import("@/integrations/supabase/client.server");
    const contactoSelect =
      "id, nombre, apellidos, email, agencia_id, notif_enabled, notif_canal_email";
    const preferirAgencia = (rows: DestinoInvitacion[] | null, agenciaId: string) =>
      (rows ?? []).find((c) => c.agencia_id === agenciaId) ?? rows?.[0] ?? null;

    let agenciaId: string;
    let rol: string;
    let actor: AgenciaActor;
    let contacto: DestinoInvitacion | null;
    let to: string | null;
    if (data.tipo === "invitacion") {
      const { data: inv } = await sb
        .from("agencia_invitaciones")
        .select("agencia_id, email, rol, estado")
        .eq("id", data.id)
        .maybeSingle();
      if (!inv) throw new Error("Invitación no encontrada");
      actor = await requireAgenciaGestion(inv.agencia_id);
      if (inv.estado !== "pendiente") throw new Error("La invitación ya no está pendiente.");
      agenciaId = inv.agencia_id;
      rol = inv.rol;
      // La cuenta debe crearse con el email de la invitación para recibir el acceso.
      to = inv.email;
      const { data: cs } = await sb
        .from("contactos")
        .select(contactoSelect)
        .eq("email_norm", inv.email.trim().toLowerCase())
        .limit(20);
      contacto = preferirAgencia(cs as DestinoInvitacion[] | null, agenciaId);
      if (!contacto)
        throw new Error("No hay ninguna ficha de contacto con el email de la invitación.");
    } else {
      const { data: m } = await sb
        .from("agencia_miembros")
        .select("agencia_id, user_id, rol")
        .eq("id", data.id)
        .maybeSingle();
      if (!m) throw new Error("Miembro no encontrado");
      actor = await requireAgenciaGestion(m.agencia_id);
      if (m.user_id === actor.userId)
        throw new Error("No puedes enviarte la invitación a ti mismo.");
      agenciaId = m.agencia_id;
      rol = m.rol;
      const { data: cs } = await sb
        .from("contactos")
        .select(contactoSelect)
        .eq("user_id", m.user_id)
        .limit(20);
      // El listado muestra el email de una de sus fichas: se envía a esa.
      contacto =
        (cs as DestinoInvitacion[] | null)?.find(
          (c) => c.email?.trim().toLowerCase() === data.email.toLowerCase(),
        ) ?? null;
      to = contacto?.email?.trim() ?? null;
    }

    if (data.tipo === "miembro" && !contacto)
      throw new Error("El email ha cambiado. Recarga el listado antes de enviar la invitación.");
    if (!contacto || !to || !z.string().email().safeParse(to).success)
      throw new Error("Este miembro no tiene un email válido para recibir la invitación.");
    if (to.toLowerCase() !== data.email.toLowerCase())
      throw new Error("El email ha cambiado. Recarga el listado antes de enviar la invitación.");
    if (contacto.notif_enabled === false || contacto.notif_canal_email === false)
      throw new Error("Este contacto tiene desactivadas las comunicaciones por email.");

    const { AGENCIA_INVITACION_COOLDOWN_MS, buildAgenciaInvitacionDraft } =
      await import("@/lib/agencia-invitacion-email.server");
    const desde = new Date(Date.now() - AGENCIA_INVITACION_COOLDOWN_MS).toISOString();
    const { data: recientes } = await sb
      .from("comunicaciones")
      .select("status, meta_data")
      .eq("contact_id", contacto.id)
      .eq("channel", "email")
      .gt("created_at", desde);
    if (
      (recientes ?? []).some(
        (r) =>
          (r.meta_data as { tipo?: string } | null)?.tipo === "agencia_invitacion" &&
          r.status !== "error",
      )
    )
      throw new Error("Ya se le ha enviado la invitación hace unos minutos.");

    const { data: agencia } = await sb
      .from("agencias")
      .select("nombre")
      .eq("id", agenciaId)
      .maybeSingle();
    if (!agencia) throw new Error("Agencia no encontrada");

    // Firma quien invita si es del equipo; si es staff de Bizaclia, el equipo de Bizaclia.
    let invitadoPor = "El equipo de Bizaclia";
    if (actor.roles[agenciaId]) {
      const { data: yo } = await sb
        .from("contactos")
        .select("nombre, apellidos")
        .eq("user_id", actor.userId)
        .limit(1)
        .maybeSingle();
      invitadoPor =
        [yo?.nombre, yo?.apellidos].filter(Boolean).join(" ").trim() ||
        `El equipo de ${agencia.nombre}`;
    }

    const { sendLoggedManagedEmail } = await import("@/lib/logged-email.server");
    return sendLoggedManagedEmail(sb, {
      contactId: contacto.id,
      to,
      actor: actor.userId,
      tipo: "agencia_invitacion",
      draft: buildAgenciaInvitacionDraft({
        conCuenta: data.tipo === "miembro",
        nombre: [contacto.nombre, contacto.apellidos].filter(Boolean).join(" ") || null,
        email: to,
        agencia: agencia.nombre,
        rol,
        invitadoPor,
      }),
      meta: {
        agencia_id: agenciaId,
        ...(data.tipo === "invitacion" ? { invitacion_id: data.id } : { miembro_id: data.id }),
      },
    });
  });

export const searchContactosConCuenta = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ q: z.string().trim().max(120) }).parse(d))
  .handler(async ({ data }) => {
    await requireStaffAg();
    const sb = getSupabaseAdmin();
    let query = sb
      .from("contactos")
      .select("user_id, nombre, apellidos, email")
      .not("user_id", "is", null)
      .limit(20);
    for (const t of searchTerms(data.q)) query = query.ilike("busqueda", `%${t}%`);
    const { data: rows, error } = await query;
    if (error) throw new Error(error.message);
    return (rows ?? []).filter((r) => r.user_id) as Array<{
      user_id: string;
      nombre: string;
      apellidos: string | null;
      email: string | null;
    }>;
  });

export const searchAgenciasLite = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        search: z.string().trim().max(100).optional().default(""),
        ids: z.array(z.string().uuid()).max(100).optional().default([]),
        limit: z.number().int().min(1).max(50).optional().default(10),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    // Staff ve todas las agencias. Miembro de agencia sólo las suyas.
    const userId = await resolveCurrentUserId();
    if (!userId) throw new Error("Unauthorized");
    const sb = getSupabaseAdmin();
    const { data: roles } = await sb
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    const rs = (roles ?? []).map((r: { role: string }) => r.role);
    const isStaff =
      rs.includes("admin") || rs.includes("customer_service") || rs.includes("agente");
    let scopedIds: string[] = [];
    if (!isStaff) {
      const { data: members } = await sb
        .from("agencia_miembros")
        .select("agencia_id")
        .eq("user_id", userId);
      scopedIds = (members ?? []).map((m: { agencia_id: string }) => m.agencia_id);
      if (scopedIds.length === 0) throw new Error("Forbidden");
    }
    const results: Array<{
      id: string;
      slug: string | null;
      nombre: string;
      email: string | null;
      telefono: string | null;
      region: string | null;
      logo_url: string | null;
      anuncios_count: number;
    }> = [];
    const seen = new Set<string>();
    const cols = "id, slug, nombre, email, telefono, region, logo_url, anuncios_count";

    if (data.ids.length) {
      let q = sb.from("agencias").select(cols).in("id", data.ids);
      if (!isStaff) q = q.in("id", scopedIds);
      const { data: rows, error } = await q;
      if (error) throw new Error(error.message);
      for (const r of rows ?? []) {
        if (seen.has(r.id)) continue;
        seen.add(r.id);
        results.push(r as any);
      }
    }

    const q = data.search;
    const terms = q ? searchTerms(q) : [];
    if (terms.length) {
      let qq = sb.from("agencias").select(cols).limit(data.limit);
      for (const t of terms) qq = qq.ilike("busqueda", `%${t}%`);
      if (!isStaff) qq = qq.in("id", scopedIds);
      const { data: rows, error } = await qq;
      if (error) throw new Error(error.message);
      for (const r of rows ?? []) {
        if (seen.has(r.id)) continue;
        seen.add(r.id);
        results.push(r as any);
      }
    } else if (!data.ids.length) {
      let qq = sb
        .from("agencias")
        .select(cols)
        .order("nombre", { ascending: true })
        .limit(data.limit);
      if (!isStaff) qq = qq.in("id", scopedIds);
      const { data: rows, error } = await qq;
      if (error) throw new Error(error.message);
      for (const r of rows ?? []) {
        if (seen.has(r.id)) continue;
        seen.add(r.id);
        results.push(r as any);
      }
    }

    return results;
  });


const publicAgenciaSelect =
  "id, slug, nombre, logo_url, url_web, region, num_empleados, descripcion_publica, estado, created_at";

function mapAgenciaPublic(r: any) {
  return {
    id: r.id,
    slug: r.slug,
    nombre: r.nombre,
    logo_url: r.logo_url ?? null,
    url_web: r.url_web ?? null,
    region: r.region ?? null,
    num_empleados: r.num_empleados ?? null,
    descripcion_publica: r.descripcion_publica ?? null,
    estado: r.estado ?? null,
    created_at: r.created_at,
  };
}

export const listAgenciasPublic = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { fetchAllPaged } = await import("@/lib/pagination.server");
  // Paginado: PostgREST corta cada lectura en 1000 filas y hay más de 9.000
  // agencias (sin esto el directorio terminaba en la "B").
  const rows = await fetchAllPaged<any>((from, to) =>
    (supabaseAdmin.from("agencias").select(publicAgenciaSelect) as any)
      .neq("estado", "bloqueada")
      .order("nombre", { ascending: true })
      .order("id", { ascending: true })
      .range(from, to),
  );
  return rows.map(mapAgenciaPublic);
});

export const getAgenciaPublic = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => Id.parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await (supabaseAdmin
      .from("agencias")
      .select(publicAgenciaSelect)
      .eq("id", data.id) as any)
      .neq("estado", "bloqueada")
      .maybeSingle();

    if (error) throw new Error(error.message);
    return row ? mapAgenciaPublic(row) : null;
  });

export const AGENCIA_ROLES = AgenciaRol.options;
export const AGENCIA_ORIGENES = AgenciaOrigen.options;
export const AGENCIA_ESTADOS = AgenciaEstado.options;
export const AGENCIA_SEGUIMIENTOS = AgenciaSeguimiento.options;


// ───────── Buscador para impersonación (staff) ─────────
export const searchAgenciasScope = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        search: z.string().trim().max(100).optional().default(""),
        ids: z.array(z.string().uuid()).max(20).optional().default([]),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    await requireStaffAg();
    const { supabaseAdmin: sb } = await import("@/integrations/supabase/client.server");
    const cols = "id, nombre, estado, miembros_count";
    const out: Array<{
      id: string;
      nombre: string;
      estado: string | null;
      miembros_count: number | null;
    }> = [];
    const seen = new Set<string>();

    if (data.ids.length) {
      const { data: rows } = await sb.from("agencias").select(cols).in("id", data.ids);
      for (const r of rows ?? []) {
        if (seen.has(r.id)) continue;
        seen.add(r.id);
        out.push(r as any);
      }
    }

    let q = sb
      .from("agencias")
      .select(cols)
      .order("estado", { ascending: true })
      .order("nombre", { ascending: true })
      .limit(20);
    if (data.search) {
      for (const t of searchTerms(data.search)) q = q.ilike("busqueda", `%${t}%`);
    }
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    for (const r of rows ?? []) {
      if (seen.has(r.id)) continue;
      seen.add(r.id);
      out.push(r as any);
    }
    // activa primero
    out.sort((a, b) => Number(b.estado === "activa") - Number(a.estado === "activa"));
    return out;
  });
